import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  filteredReceipts: [],
  filteredItems: [],
  filteredOrderItems: [],
  filteredIngredients: [],
  filteredOrders: [],
  filteredExpenses: [],
  filteredCashes: [],
  filteredTables: [],
  filteredDayReports: [],
  filteredItemReports: [],
  filteredNewPurchases: [],
  filteredOpenPurchases: [],
  filteredClosePurchases: [],
  filteredInventoryAdjusts: [],
  filteredInventoryInOutReports: [],
};

const filterSlice = createSlice({
  name: "filter",
  initialState,
  reducers: {
    filterReceipts(state, action) {
      const { search, orders = [] } = action.payload;
      state.filteredReceipts = orders?.filter((order) =>
        String(order?.orderNo).toLowerCase().includes(search?.toLowerCase())
      );
    },
    filterItems(state, action) {
      const { search, items } = action.payload;
      state.filteredItems = items?.filter((item) =>
        item?.name?.toLowerCase().includes(search.toLowerCase())
      );
    },
    // filterExpenses(state, action) {
    //   const { search, expenses } = action.payload;
    //   state.filteredExpenses = expenses?.filter((expense) =>
    //     expense?.description?.toLowerCase().includes(search.toLowerCase())
    //   );
    // },
    filterExpenses(state, action) {
      const { search, expenses } = action.payload;

      const list = Array.isArray(expenses) ? expenses : [];

      state.filteredExpenses = list.filter((expense) =>
        expense?.description?.toLowerCase().includes(search.toLowerCase())
      );
    },
    filterCashes(state, action) {
      const { search, cashes } = action.payload;
      state.filteredCashes = cashes?.filter((cash) =>
        cash?.description?.toLowerCase().includes(search.toLowerCase())
      );
    },
    filterIngredients(state, action) {
      const { search, ingredients } = action.payload;
      state.filteredIngredients = ingredients?.filter((ingredient) =>
        ingredient?.name?.toLowerCase().includes(search.toLowerCase())
      );
    },
    filterOrders(state, action) {
      const { search, pendingOrders } = action.payload;
      const searchLower = search.toLowerCase();

      state.filteredOrders = pendingOrders?.filter((pendingOrder) => {
        const orderNo = pendingOrder.orderNo
          ? String(pendingOrder.orderNo).toLowerCase()
          : "";
        const tableNo = pendingOrder.tableNo
          ? String(pendingOrder.tableNo).toLowerCase()
          : "";
        const username = pendingOrder.username
          ? pendingOrder.username.toLowerCase()
          : "";

        return (
          orderNo.includes(searchLower) ||
          tableNo.includes(searchLower) ||
          username.includes(searchLower)
        );
      });
    },

    filterNewPurchases(state, action) {
      const { search, newPurchases } = action.payload;

      const searchLower = search?.toLowerCase() || "";

      state.filteredNewPurchases =
        newPurchases?.filter((newPurchase) => {
          const purchaseNo = newPurchase?.purchaseNo
            ? String(newPurchase.purchaseNo).toLowerCase()
            : "";
          return purchaseNo.includes(searchLower);
        }) || [];
    },

    filterOpenPurchases(state, action) {
      const { searchOpen, openPurchases } = action.payload;

      const searchLower = searchOpen?.toLowerCase() || "";

      state.filteredOpenPurchases =
        openPurchases?.filter((openPurchase) => {
          const purchaseNo = openPurchase?.purchaseNo
            ? String(openPurchase.purchaseNo).toLowerCase()
            : "";
          return purchaseNo.includes(searchLower);
        }) || [];
    },

    filterClosePurchases(state, action) {
      const { searchClose, closePurchases } = action.payload;

      const searchLower = searchClose?.toLowerCase() || "";

      state.filteredClosePurchases =
        closePurchases?.filter((closePurchase) => {
          const purchaseNo = closePurchase?.purchaseNo
            ? String(closePurchase.purchaseNo).toLowerCase()
            : "";
          return purchaseNo.includes(searchLower);
        }) || [];
    },
    filterInventoryAdjusts(state, action) {
      const { searchInventoryAdjust, inventoryAdjusts } = action.payload;

      const searchLower = searchInventoryAdjust?.toLowerCase() || "";

      state.filteredInventoryAdjusts =
        inventoryAdjusts?.filter((inventoryAdjust) => {
          const inventoryAdjustNo = inventoryAdjust?.inventoryAdjustNo
            ? String(inventoryAdjust.inventoryAdjustNo).toLowerCase()
            : "";
          return inventoryAdjustNo.includes(searchLower);
        }) || [];
    },

    filterOrderItems(state, action) {
      const { search, data } = action.payload;
      const orderItems = Array.isArray(data) ? data : Object.values(data || {});
      state.filteredOrderItems = orderItems?.filter((item) =>
        item?.name?.toLowerCase().includes(search.toLowerCase())
      );
    },

    filterTables(state, action) {
      const { search, tables } = action.payload;
      const searchLower = search.toLowerCase();

      state.filteredTables = tables?.filter((table) => {
        const tableNo = table.tableNo
          ? String(table.tableNo).toLowerCase()
          : "";
        const tableStatus = table.tableStatus
          ? String(table.tableStatus).toLowerCase()
          : "";

        return (
          tableNo.includes(searchLower) || tableStatus.includes(searchLower)
        );
      });
    },

    filterPreTables(state, action) {
      const { searchPre, tables } = action.payload;
      const searchLower = searchPre.toLowerCase();

      state.filteredTables = tables?.filter((table) => {
        const tableNo = table.tableNo
          ? String(table.tableNo).toLowerCase()
          : "";
        const tableStatus = table.tableStatus
          ? String(table.tableStatus).toLowerCase()
          : "";

        return (
          tableNo.includes(searchLower) || tableStatus.includes(searchLower)
        );
      });
    },
    filterDayReports(state, action) {
      const { searchDayReport, dayReports } = action.payload;

      if (!searchDayReport) {
        state.filteredDayReports = dayReports;
        return;
      }

      const searchDate = new Date(searchDayReport);
      if (isNaN(searchDate)) {
        console.error("Invalid search date:", searchDayReport);
        return;
      }

      const formattedSearchDate = searchDate.toISOString().split("T")[0];

      state.filteredDayReports = dayReports?.filter((dayReport) => {
        if (!dayReport?.fromDateTime) return false;

        const reportDate = new Date(dayReport.fromDateTime);
        if (isNaN(reportDate)) {
          console.error("Invalid report date:", dayReport.fromDateTime);
          return false;
        }

        return reportDate.toISOString().split("T")[0] === formattedSearchDate;
      });
    },

    filterItemReports(state, action) {
      const { searchItemReport, itemReports } = action.payload;

      if (!searchItemReport) {
        state.filteredItemReports = itemReports;
        return;
      }

      const searchDate = new Date(searchItemReport);
      if (isNaN(searchDate)) {
        console.error("Invalid search date:", searchItemReport);
        return;
      }

      const formattedSearchDate = searchDate.toISOString().split("T")[0];

      state.filteredItemReports = itemReports?.filter((itemReport) => {
        if (!itemReport?.fromDateTime) return false;

        const reportDate = new Date(itemReport.fromDateTime);
        if (isNaN(reportDate)) {
          console.error("Invalid report date:", itemReport.fromDateTime);
          return false;
        }

        return reportDate.toISOString().split("T")[0] === formattedSearchDate;
      });
    },
    filterInventoryInOutReport(state, action) {
      const { searchInventoryInOutReport, inventoryInOutReports } =
        action.payload;

      if (!searchInventoryInOutReport) {
        state.filteredInventoryInOutReports = inventoryInOutReports;
        return;
      }

      const searchDate = new Date(searchInventoryInOutReport);
      if (isNaN(searchDate)) {
        console.error("Invalid search date:", searchInventoryInOutReport);
        return;
      }

      const formattedSearchDate = searchDate.toISOString().split("T")[0];

      state.filteredInventoryInOutReports = inventoryInOutReports?.filter(
        (inventoryInOutReport) => {
          if (!inventoryInOutReport?.fromDateTime) return false;

          const reportDate = new Date(inventoryInOutReport.fromDateTime);
          if (isNaN(reportDate)) {
            console.error(
              "Invalid report date:",
              inventoryInOutReport.fromDateTime
            );
            return false;
          }

          return reportDate.toISOString().split("T")[0] === formattedSearchDate;
        }
      );
    },
  },
});

// Action and Selector Exports
export const {
  filterReceipts,
  filterItems,
  filterIngredients,
  filterOrders,
  filterOrderItems,
  filterExpenses,
  filterCashes,
  filterTables,
  filterPreTables,
  filterDayReports,
  filterItemReports,
  filterNewPurchases,
  filterClosePurchases,
  filterOpenPurchases,
  filterInventoryInOutReport,
  filterInventoryAdjusts,
} = filterSlice.actions;
export const selectFilteredReceipts = (state) => state.filter.filteredReceipts;
export const selectFilteredExpenses = (state) => state.filter.filteredExpenses;
export const selectFilteredCashes = (state) => state.filter.filteredCashes;
export const selectFilteredIngredients = (state) =>
  state.filter.filteredIngredients;
export const selectFilteredItems = (state) => state.filter.filteredItems;
export const selectFilteredOrders = (state) => state.filter.filteredOrders;
export const selectFilteredNewPurchases = (state) =>
  state.filter.filteredNewPurchases;
export const selectFilteredOpenPurchases = (state) =>
  state.filter.filteredOpenPurchases;
export const selectFilteredClosePurchases = (state) =>
  state.filter.filteredClosePurchases;
export const selectFilteredTables = (state) => state.filter.filteredTables;
export const selectFilteredDayReports = (state) =>
  state.filter.filteredDayReports;
export const selectFilteredItemReports = (state) =>
  state.filter.filteredItemReports;
export const selectFilteredInventoryInOutReports = (state) =>
  state.filter.filteredInventoryInOutReports;
export const selectFilteredInventoryAdjusts = (state) =>
  state.filter.filteredInventoryAdjusts;
export const selectFilteredOrderItems = (state) =>
  state.filter.filteredOrderItems;

export default filterSlice.reducer;
